import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button, Dialog, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { X } from 'lucide-react';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { FM } from './forms/messages';
import { FormActions } from './forms/FormActions';
import type { FormActionsProps } from './forms/types';
import './forms/styles';

const m = defineMessages({
  close: {
    id: 'common.ui.detailDrawer.close',
    defaultMessage: 'Закрыть карточку',
    description: 'aria-label кнопки-крестика боковой панели с карточкой объекта',
  },
});

interface DrawerApi {
  /** Закрыть так же, как Escape: с вопросом, если в форме есть несохранённый ввод. */
  requestClose: () => void;
  /** Форма внутри ящика сообщает, есть ли в ней несохранённый ввод. */
  setDirty: (dirty: boolean) => void;
}

const DrawerContext = createContext<DrawerApi | null>(null);

/**
 * Для кнопки «Отмена» внутри формы бокового ящика: закрывать ТАК ЖЕ, как
 * Escape и крестик, — с вопросом при несохранённом вводе. Вне ящика — пустая
 * функция.
 */
export function useDrawerClose(): () => void {
  return useContext(DrawerContext)?.requestClose ?? (() => undefined);
}

/**
 * Форма, вложенная в ящик, говорит ящику о несохранённом вводе — когда `isDirty`
 * нельзя передать пропом (ящик рисует один компонент, форму другой).
 */
export function useDrawerDirty(dirty: boolean): void {
  const setDirty = useContext(DrawerContext)?.setDirty;
  useEffect(() => {
    setDirty?.(dirty);
    return () => setDirty?.(false);
  }, [dirty, setDirty]);
}

/**
 * Ввод изменён относительно момента открытия формы. Снимок значений берётся
 * при первом показе; сравнение — по JSON, поэтому в `value` кладут только то,
 * что вводит человек (не загруженные справочники).
 */
export function useChangedFromOpen(value: unknown): boolean {
  const text = JSON.stringify(value);
  const first = useRef(text);
  return text !== first.current;
}

/** Кнопки формы ящика: «Отмена» закрывает с тем же вопросом, что Escape. */
export function DrawerFormActions({ cancel, ...rest }: Omit<FormActionsProps, 'secondary'> & { cancel: string }) {
  const close = useDrawerClose();
  return <FormActions {...rest} secondary={{ label: cancel, onPress: close }} />;
}

interface DetailDrawerProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /**
   * В форме внутри ящика есть несохранённый ввод (как `isDirty` у `FormDialog`):
   * Escape, клик вне ящика и крестик сначала спрашивают «Закрыть без
   * сохранения?». Ящик без формы флаг не задаёт и закрывается сразу.
   */
  isDirty?: boolean;
}

export function DetailDrawer({ title, onClose, children, isDirty = false }: DetailDrawerProps) {
  const t = useT();
  const [asking, setAsking] = useState(false);
  const [reported, setReported] = useState(false);
  const dirty = isDirty || reported;

  // Ввод сохранён или сброшен — висящий вопрос теряет смысл.
  useEffect(() => {
    if (!dirty) setAsking(false);
  }, [dirty]);

  const api = useMemo<DrawerApi>(
    () => ({
      requestClose: () => {
        if (dirty) setAsking(true);
        else onClose();
      },
      setDirty: setReported,
    }),
    [dirty, onClose],
  );

  return (
    <DrawerContext.Provider value={api}>
      <ModalOverlay className="astra-drawer-overlay" isOpen isDismissable onOpenChange={open => { if (!open) api.requestClose(); }}>
        <Modal className="astra-drawer">
          <Dialog>
            <header>
              <Heading slot="title">{title}</Heading>
              <Button aria-label={t(m.close)} onPress={api.requestClose}><X size={20} /></Button>
            </header>
            <div className="astra-drawer-content">{children}</div>
          </Dialog>
        </Modal>
      </ModalOverlay>

      <ModalOverlay className="fm-overlay fm-confirm-overlay" isOpen={asking} isDismissable onOpenChange={open => { if (!open) setAsking(false); }}>
        <Modal className="fm-confirm">
          <Dialog role="alertdialog" className="fm-confirm-dialog">
            <Heading slot="title" className="fm-confirm__title">{t(FM.discardTitle)}</Heading>
            <p>{t(FM.discardBody)}</p>
            <div className="fm-actions">
              <div className="fm-actions__buttons">
                <Button autoFocus className="btn btn--ghost focus-ring" onPress={() => setAsking(false)}>{t(FM.discardKeep)}</Button>
                <Button
                  className="btn btn--secondary focus-ring"
                  onPress={() => {
                    setAsking(false);
                    onClose();
                  }}
                >
                  {t(FM.discardConfirm)}
                </Button>
              </div>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DrawerContext.Provider>
  );
}
